/**
 * @file 请求配置
 * @description umi-request 请求/响应拦截器配置，包含错误处理、Token 管理、数据加密等
 * @doc https://umijs.org/docs/max/request#配置
 */
import { useUserStore } from "@/store";
import { decrypt, encrypt } from "@/utils/util";
import type { RequestOptions } from "@@/plugin-request/request";
import { getIntl, history, type RequestConfig } from "@umijs/max";
import { message, notification } from "antd";
import { CURRENT_SITE_CONFIG } from "@/utils/constants";
// ==================== 环境判断 ====================

/** 是否为 Mock 环境（未设置 MOCK=none 时为 Mock 环境） */
const isMockEnv = process.env.MOCK !== "none";

/** 是否为开发环境（仅开发环境打印接口日志） */
// const isDevEnv = process.env.NODE_ENV === "development";

/**
 * 统一接口日志
 * @description 全项目唯一的接口打印入口，输出「接口名称 / 参数 / 返回」，仅开发环境生效
 * @param type   'req' 请求 | 'res' 返回
 * @param url    接口地址（接口名称）
 * @param payload 请求参数或返回数据
 */
const logApi = (type: "req" | "res", url?: string, payload?: any) => {
  // if (!isDevEnv) return;
  const isReq = type === "req";
  console.log(
    `%c[API ${isReq ? "→ 请求" : "← 返回"}] %c${url ?? ""}`,
    `color:${isReq ? "#1677ff" : "#52c41a"};font-weight:600`,
    "color:inherit",
    payload
  );
};
// ==================== 类型定义 ====================
/** 错误展示类型 */
enum ErrorShowType {
  /** 静默处理，不展示 */
  SILENT = 0,
  /** 警告提示 */
  WARN_MESSAGE = 1,
  /** 错误提示 */
  ERROR_MESSAGE = 2,
  /** 通知提示 */
  NOTIFICATION = 3,
  /** 重定向 */
  REDIRECT = 9,
}

/** 后端响应数据结构 */
interface ResponseStructure {
  config: any;
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

// ==================== 常量配置 ====================

/** 默认 AES 加密密钥 */
const DEFAULT_AES_KEY = "3f162f46d3b2f9ub";

/** 解密 AES 密钥 */
const DECRYPT_AES_KEY = "r922Rv2XBWWxEj52";

/** 需要显示操作成功提示的接口关键词 */
const SUCCESS_MSG_KEYWORDS = [
  "/add",
  "/update",
  "/edit",
  "/delete",
  "/remove",
  "/del",
  "/gameEdit",
  "/forceCancel",
  "/forceApprove",
  "/ignore",
  "/audit",
];

/** Token 过期错误码 */
const TOKEN_EXPIRED_CODES = ["1004", "10044"];

/** 无权限错误码 */
const NO_PERMISSION_CODE = "1002";

/** 成功状态码 */
const SUCCESS_CODE = "200";

// ==================== 工具函数 ====================

/** 防抖显示错误消息（5秒内只显示一次） */
let lastErrorMsgTimer: ReturnType<typeof setTimeout> | null = null;
const showThrottledError = (msg: string) => {
  if (lastErrorMsgTimer) return;
  message.error(msg);
  lastErrorMsgTimer = setTimeout(() => {
    lastErrorMsgTimer = null;
  }, 5000);
};

/** 判断是否需要显示操作成功提示 */
const shouldShowSuccessMsg = (url: string): boolean => {
  return SUCCESS_MSG_KEYWORDS.some((keyword) => url.includes(keyword));
};

/** 获取默认请求头 */
const getDefaultHeaders = () => ({
  languageType:
    localStorage.getItem("lang") ||
    localStorage.getItem("defaultLang") ||
    "zh_CN",
    timezone: "UTC+08:00",
    siteNation: CURRENT_SITE_CONFIG.siteNation,
  // timezone: localStorage.getItem("timezone") || "UTC+08:00",
  // siteNation: localStorage.getItem("siteNation") || "Africa",
});

// ==================== 错误处理 ====================

/** 处理业务错误 */
const handleBizError = (errorInfo: ResponseStructure) => {
  const { errorMessage, errorCode, showType } = errorInfo;

  const handlers: Record<ErrorShowType, () => void> = {
    [ErrorShowType.SILENT]: () => {},
    [ErrorShowType.WARN_MESSAGE]: () => message.warning(errorMessage),
    [ErrorShowType.ERROR_MESSAGE]: () => message.error(errorMessage),
    [ErrorShowType.NOTIFICATION]: () =>
      notification.open({ description: errorMessage, message: errorCode }),
    [ErrorShowType.REDIRECT]: () => {
      // TODO: 实现重定向逻辑
    },
  };

  const handler = handlers[showType as ErrorShowType];
  handler ? handler() : message.error(errorMessage);
};

/** 处理 HTTP 错误 */
const handleHttpError = (error: any) => {
  const intl = getIntl();
  if (error.response) {
    const errorMsg =
      error.response.data?.msg ||
      error.response.data?.message ||
      `Response status: ${error.response.status}`;
    message.error(errorMsg);
  } else if (error.request) {
    message.error(
      intl.formatMessage({
        id: "common.noResponse",
        defaultMessage: "None response! Please retry.",
      })
    );
  } else {
    message.error(
      intl.formatMessage({
        id: "common.requestError",
        defaultMessage: "Request error, please retry.",
      })
    );
  }
};

// ==================== 响应处理 ====================

/** 处理 Token 过期 */
const handleTokenExpired = (msg: string) => {
  history.push("/user/login");
  useUserStore.getState().logout();
  showThrottledError(msg);
};

/** 处理响应状态码 */
const handleResponseCode = (data: any, url: string, responseType?: string) => {
  const code = data?.code;
  const intl = getIntl();

  // Token 过期
  if (TOKEN_EXPIRED_CODES.includes(code)) {
    handleTokenExpired(data?.msg);
    return;
  }

  // 无权限
  if (code === NO_PERMISSION_CODE) {
    message.error(intl.formatMessage({ id: "common.noPermission" }));
    return;
  }

  // 业务错误（排除 blob 响应）
  if (code !== SUCCESS_CODE && responseType !== "blob" && data?.msg) {
    message.error(data.msg);
    return;
  }

  // 操作成功提示
  if (code === SUCCESS_CODE && shouldShowSuccessMsg(url)) {
    message.success(intl.formatMessage({ id: "common.operationSuccessful" }));
  }
};

// ==================== 导出配置 ====================

export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = "BizError";
        error.info = { errorCode, errorMessage, showType, data };
        throw error;
      }
    },

    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.name === "BizError") {
        handleBizError(error.info);
      } else {
        handleHttpError(error);
      }
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      // const intl = getIntl();
      const { url } = config;
      const aesKey = DEFAULT_AES_KEY;

      // 设置请求头
      if (config.headers) {
        // 调用方已显式传入 Authorization 时以其为准
        // （登录流程中的谷歌验证码接口要用后端下发的临时 token，而非 store 里的登录态）
        if (!config.headers["Authorization"]) {
          const token = useUserStore.getState().token;
          if (token) {
            config.headers["Authorization"] = token;
          }
        }

        const defaultHeaders = getDefaultHeaders();
        config.headers["languageType"] = defaultHeaders.languageType;
        config.headers["timezone"] = defaultHeaders.timezone;
        config.headers["siteNation"] = defaultHeaders.siteNation;
        config.headers["Content-Type"] =
          config.headers["Content-Type"] || "application/json;charset=UTF-8";
      }
      // 统一日志：打印接口名称与请求参数（加密前）
      logApi("req", url, config.data);

      // 加密请求数据（FormData 除外，仅 Mock 环境跳过加密）
      if (!(config.data instanceof FormData)) {
        if (!isMockEnv) {
          config.data = encrypt(config.data, aesKey);
        }
      }

      return { ...config, url };
    },
  ],

  responseInterceptors: [
    (response) => {
      const intl = getIntl();
      const { config } = response as unknown as ResponseStructure;
      let { data } = response as unknown as ResponseStructure;
      // console.log(response, "response");
      // 解密响应数据（仅当返回数据是字符串时才解密，对象不需要解密）
      if (!isMockEnv && typeof data === "string" && data) {
        // 当接口返回没有 code 和 data 时（返回的是加密字符串），对返回数据进行解密
        try {
          data = decrypt(data, DECRYPT_AES_KEY);
          (response as any).data = data;
        } catch {}
      }

      // 统一日志：打印接口名称与返回数据（解密后）
      logApi("res", config?.url, data);

      // 更新 Token（从响应头获取）
      const token = response.headers.authorization;
      if (token) {
        useUserStore.getState().setToken(token, 1);
      }

      // 处理请求失败
      if (data?.success === false) {
        message.error(intl.formatMessage({ id: "common.requestFail" }));
        return response;
      }

      // 处理响应状态码
      handleResponseCode(data, config.url, config.responseType);

      // 不默认处理消息提示
      if (config?.notShowMsg === true) {
        return response;
      }
      if (data?.success === false) {
        message.error(
          data?.errorMessage ?? intl.formatMessage({ id: "common.requestFail" })
        );
        return response;
      }

      return response;
    },
  ],
};
